import Constants from "utils/Constants";
import { Fragment, useMemo } from "react";
import _ from "underscore";
import { Card, CardBody, Row, Col, Button, Input, Badge } from "reactstrap";
import FilterSelectInput from "./FilterSelectInput";
import { Tooltip, Whisper } from "rsuite";
import useModule from "hooks/useModule";
import pluralize from "pluralize";
import Phone from "components/Inputs/Phone";
import InputFormatter from "components/Records/Formatters/InputFormatter";
import { useAtomValue } from "jotai";
import { IsMobileAtom } from "../../App";
import { timeData, variableData } from "../../utils/DynamicValues";
import { cloudRelatedRecordsAtom } from "../../atoms/GlobalAtoms";

const FilterCard = ({
  currentModule,
  filters,
  filter,
  groupIndex,
  isLastCard,
  availableFields,
  showVariables,
  useVariables,
  handleSelectChange,
  handleVariableToggle,
  handleFilterInputChange,
  handleAndClick,
  handleOrClick,
  subitemFieldsOnly,
}) => {
  const relatedRecords = useAtomValue(cloudRelatedRecordsAtom);
  const mobileScreen = useAtomValue(IsMobileAtom);
  const { getModule } = useModule();

  if (!filter.Group) {
    let temp = filter;

    filter.Filters = [temp];

    filter.Group = true;
  }

  const groupFilters = filter.Filters;

  const getFieldByAPIName = (fieldAPIName) => {
    const matchedField = _.findWhere(availableFields[0], {
      APIName: fieldAPIName,
    });
    return matchedField ?? null;
  };

  const getRecordSummaryField = (moduleAPIName, fieldAPIName) => {
    const module = getModule(moduleAPIName);
    const matchedField = _.findWhere(module.Fields, { APIName: fieldAPIName });
    return matchedField;
  };

  const dynamicValues = useMemo(() => {
    if (!relatedRecords?.length) return variableData;

    const cloudValues = relatedRecords.map((relatedRecord) => {
      return {
        id: "cloud",
        label: `Cloud's ${pluralize.singular(relatedRecord._ModuleAPIName)}`,
        value: `{{cloud.${relatedRecord._ModuleAPIName}}}`,
      };
    });

    return [...variableData, ...cloudValues];
  }, [relatedRecords]);

  const getSelectComponent = (groupIndex, filterIndex, selectType) => {
    const currentFilter = filters[groupIndex].Filters[filterIndex];
    return (
      <FilterSelectInput
        currentFilter={currentFilter}
        filters={filters}
        handleSelectChange={handleSelectChange}
        groupIndex={groupIndex}
        filterIndex={filterIndex}
        selectType={selectType}
        availableFields={availableFields}
        getFieldByAPIName={getFieldByAPIName}
        getRecordSummaryField={getRecordSummaryField}
        subitemFieldsOnly={subitemFieldsOnly}
      />
    );
  };

  const getIsFilterInputDisabled = (filter) => {
    if (filter.Field === "") {
      return {
        disabled: true,
        text: "Select a field first",
      };
    }

    if (filter.Method === "is-empty" || filter.Method === "is-not-empty") {
      return {
        disabled: true,
        text: "--",
      };
    }

    return {
      disabled: false,
    };
  };

  const getFilterInputComponent = (groupIndex, filterIndex) => {
    const currentFilter = filters[groupIndex].Filters[filterIndex];
    let source = currentFilter.Source;
    let method = currentFilter.Method;
    let fieldAPIName = currentFilter.Field;
    const splitString = fieldAPIName.split(".");
    let field = getFieldByAPIName(splitString[0]);
    let fieldCopy = { ...(field || {}) };
    let fieldType = field?.FieldType ?? "";

    // Handle Subitem Fields. ex: Company.Name
    if (splitString.length > 1 && field.FieldType !== "Location") {
      const parentField = getFieldByAPIName(splitString[0]);

      let subitemField = {};
      if (parentField.RecordSummary && source) {
        subitemField = getRecordSummaryField(source, splitString[1]);
      } else {
        subitemField = _.findWhere(parentField.SubitemFields, {
          APIName: splitString[1],
        });
      }

      fieldCopy = subitemField;
      fieldType = subitemField.FieldType;
    }

    if (
      showVariables &&
      useVariables[groupIndex][filterIndex] &&
      fieldType === "DateTime"
    ) {
      return (
        <Input
          bsSize="sm"
          type="select"
          placeholder="Choose A Dynamic Value"
          style={{ width: "100%" }}
          defaultValue=""
          onChange={(e) => {
            const value = e.target.value;
            handleFilterInputChange(
              field,
              value,
              "Value",
              groupIndex,
              filterIndex
            );
          }}
          value={currentFilter?.Value}
        >
          <option value="" disabled>
            Choose a Dynamic Value
          </option>
          {timeData?.map((val) => (
            <option key={val?.value} value={val?.value}>
              {val.label}
            </option>
          ))}
        </Input>
      );
    }

    // Return text component if show variables is selected
    if (showVariables && useVariables[groupIndex][filterIndex]) {
      return (
        <>
          <Input
            bsSize="sm"
            type="select"
            placeholder="Choose A Dynamic Value"
            style={{ width: "100%" }}
            onChange={(e) => {
              handleFilterInputChange(
                field,
                e?.target?.value,
                "Value",
                groupIndex,
                filterIndex
              );
            }}
            value={currentFilter?.Value || ""}
          >
            <option value="" disabled>
              Choose a Dynamic Value
            </option>
            {dynamicValues?.map((val) => (
              <option key={val?.value} value={val?.value}>
                {val.label}
              </option>
            ))}
          </Input>
        </>
      );
    }

    if (
      fieldType === Constants.FIELDTYPES.PHONE ||
      fieldType === Constants.FIELDTYPES.EMAIL ||
      fieldType === Constants.FIELDTYPES.URL
    ) {
      fieldCopy.FieldType = Constants.FIELDTYPES.TEXT;
    }

    if (
      fieldType === Constants.FIELDTYPES.RICH_TEXT ||
      fieldType === Constants.FIELDTYPES.LOCATION
    ) {
      if (method === Constants.FILTERTYPES.CONTAINS.Value) {
        fieldCopy.FieldType = Constants.FIELDTYPES.TEXT;
      }
    }

    if (fieldType === Constants.FIELDTYPES.LOCATION) {
      fieldCopy.FieldType =
        splitString.length > 1
          ? Constants.FIELDTYPES.TEXT
          : Constants.FIELDTYPES.LOCATION;
    }

    if (
      method === Constants.FILTERTYPES.BETWEEN.Value ||
      method === Constants.FILTERTYPES.NOT_BETWEEN.Value
    ) {
      const minText =
        fieldType === Constants.FIELDTYPES.DATE ||
        fieldType === Constants.FIELDTYPES.DATE_TIME ||
        fieldType === Constants.FIELDTYPES.TIME
          ? "Start"
          : "Min";
      const maxText =
        fieldType === Constants.FIELDTYPES.DATE ||
        fieldType === Constants.FIELDTYPES.DATE_TIME ||
        fieldType === Constants.FIELDTYPES.TIME
          ? "End"
          : "Max";

      return (
        <div style={{ maxWidth: "300px" }}>
          <div className="d-flex align-items-center mb-2">
            <div className="ml-2 w-25">
              <h5 className="mb-0 font-weight-500">{minText}:</h5>
            </div>
            <div style={{ width: "70%" }}>
              <InputFormatter
                module={currentModule || null}
                field={fieldCopy}
                value={currentFilter.Min}
                onChange={(field, value) =>
                  handleFilterInputChange(
                    field,
                    value,
                    "Min",
                    groupIndex,
                    filterIndex
                  )
                }
                inline={true}
                filter={true}
              />
            </div>
          </div>
          <div className="d-flex align-items-center">
            <div className="ml-2 w-25">
              <h5 className="mb-0 font-weight-500">{maxText}:</h5>
            </div>
            <div style={{ width: "70%" }}>
              <InputFormatter
                module={currentModule || null}
                field={fieldCopy}
                value={currentFilter.Max}
                onChange={(field, value) =>
                  handleFilterInputChange(
                    field,
                    value,
                    "Max",
                    groupIndex,
                    filterIndex
                  )
                }
                inline={true}
                filter={true}
              />
            </div>
          </div>
        </div>
      );
    }

    if (
      fieldType === Constants.FIELDTYPES.PHONE &&
      currentFilter.Method !== "contains"
    ) {
      return (
        <Phone
          existingValue={currentFilter.Value}
          field={fieldCopy}
          inputSize="sm"
          onChange={(val) => {
            handleFilterInputChange(
              field,
              val,
              "Value",
              groupIndex,
              filterIndex
            );
          }}
        />
      );
    }

    return (
      <div className="w-100">
        <InputFormatter
          module={currentModule || null}
          field={fieldCopy}
          value={currentFilter.Value}
          onChange={(field, value) => {
            handleFilterInputChange(
              field,
              value,
              "Value",
              groupIndex,
              filterIndex
            );
          }}
          inline={true}
          filter={true}
        />
      </div>
    );
  };

  return (
    <>
      <Card className={`my-2 bg-white border-lighter shadow-none border`}>
        <CardBody className="p-2">
          <div className="">
            <Row className="align-items-center">
              <Col xs="">
                <Badge
                  color="info"
                  className={`text-white ${
                    groupFilters?.length > 1 ? "bg-primary" : "bg-white"
                  }`}
                  style={{ fontSize: "12px" }}
                >
                  And
                </Badge>
              </Col>
              <Col xs="auto">
                <Whisper
                  placement={"autoHorizontal"}
                  trigger="hover"
                  disabled={filters.length === 1}
                  speaker={
                    <Tooltip>
                      Remove {groupFilters?.length > 1 ? "all " : ""}
                      {groupFilters?.length}{" "}
                      {pluralize("condition", groupFilters?.length)}
                    </Tooltip>
                  }
                >
                  <Button
                    size="sm"
                    color="danger"
                    outline
                    className="btn-icon-only"
                    disabled={filters.length === 1}
                    onClick={() => handleOrClick(groupIndex - 1, false)}
                  >
                    <i className="mdi mdi-delete-outline"></i>
                  </Button>
                </Whisper>
              </Col>
            </Row>
          </div>
          <div
            className="pl-2 ml-2 mt--2 pt-3"
            style={{
              borderLeft:
                groupFilters?.length > 1
                  ? "3px solid var(--primary)"
                  : "3px solid var(--white)",
            }}
          >
            {groupFilters.map((filter, filterIndex) => {
              const isDisabled = getIsFilterInputDisabled(filter);

              return (
                <Fragment key={filterIndex}>
                  <div style={{ position: "relative" }}>
                    <div
                      style={{
                        position: "absolute",
                        width: "10px",
                        height: 4,
                        left: -8,
                        top: 12,
                      }}
                      className={`${
                        groupFilters?.length > 1 ? "bg-primary" : "bg-white"
                      }`}
                    ></div>
                    <Row
                      className={`d-flex ${
                        mobileScreen ? "flex-column" : "flex-row"
                      } w-100 m-0 p-0`}
                    >
                      <Col className="m-0 p-0 pr-1 mb-1">
                        {/* FIELD TYPE SELECT */}
                        {getSelectComponent(groupIndex, filterIndex, "Field")}
                      </Col>
                      <Col className="m-0 p-0 pr-1 mb-1">
                        {/* METHOD SELECT */}
                        {getSelectComponent(groupIndex, filterIndex, "Method")}
                      </Col>
                      {isDisabled.disabled ? (
                        <Col className="m-0 p-0 pr-1 mb-1">
                          <Input
                            type="text"
                            placeholder={isDisabled.text}
                            disabled
                            bsSize="sm"
                          />
                        </Col>
                      ) : (
                        <Col
                          className="m-0 p-0 pr-1 mb-1"
                          style={{ maxWidth: mobileScreen ? "100%" : 158 }}
                        >
                          <Col className="d-flex align-items-center p-0">
                            {getFilterInputComponent(groupIndex, filterIndex)}
                          </Col>
                        </Col>
                      )}
                      <div className="pl-1">
                        {showVariables && (
                          <Button
                            size="sm"
                            className="btn-icon-only"
                            outline={!useVariables[groupIndex][filterIndex]}
                            onClick={() =>
                              handleVariableToggle(groupIndex, filterIndex)
                            }
                          >
                            <i className="mdi mdi-xml"></i>
                          </Button>
                        )}
                        <Button
                          size="sm"
                          color="secondary"
                          outline
                          disabled={groupFilters.length === 1}
                          className="btn-icon-only"
                          onClick={() =>
                            handleAndClick(groupIndex, filterIndex, false)
                          }
                        >
                          <i className="mdi mdi-close-circle-outline"></i>
                        </Button>
                      </div>
                    </Row>
                  </div>
                </Fragment>
              );
            })}
          </div>
          <div className="ml-3 pl-1 mt-2">
            <Button
              size="sm"
              outline
              className=""
              onClick={() =>
                handleAndClick(groupIndex, groupFilters.length, true)
              }
            >
              <i className="mdi mdi-plus-circle-outline mr-1"></i>
              Add Condition
            </Button>
          </div>
        </CardBody>
      </Card>
      <div>
        {isLastCard ? (
          <>
            <Button
              className="ml-0"
              outline
              onClick={() => handleOrClick(groupIndex, isLastCard)}
              size="sm"
            >
              <i
                className="mdi mdi-plus-circle-outline mr-1"
                style={{
                  fontSize: 16,
                  position: "relative",
                  top: 1,
                  lineHeight: 1,
                }}
              ></i>{" "}
              <span style={{ position: "relative", top: -1 }}>Add Group</span>
            </Button>
          </>
        ) : (
          <>
            <div className="my-2">
              <Badge
                color="secondary"
                className="text-white px-2 ml-2"
                style={{ fontSize: "12px" }}
              >
                OR
              </Badge>
            </div>
          </>
        )}
      </div>
    </>
  );
};

export default FilterCard;
